import { css } from '@emotion/react';

import { useRouter } from 'next/router';

import { useQuery } from 'react-query';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { CurationViewType, useAnalytics } from '@utils/analytics';

import { fetchBanners } from '../../api/common';
import { IBanner } from '../../model';
import { useWebView } from '../../utils/webview';
import Border, { BorderType } from '../layout/Border';
import { EFinderImageSizes, OptimizeEFinderImage } from './EFinderImage';

export const enum BannerSize {
  curation = '38.8889%',
  promotion = '20%',
  home = 204,
  playHome = 240,
}

export interface BannerProps {
  name: string;
  size?: BannerSize;
  items: IBanner[];
  isBorder?: boolean;
  priority?: boolean;
  autoPlay?: boolean;
}

const bannerItemCSS = (size: number | string) => css`
  position: relative;
  padding-bottom: ${typeof size === 'number' ? `${size}px` : size};
`;

const Banner = ({
  name,
  isBorder = true,
  size,
  items,
  priority = false,
  autoPlay = true,
}: BannerProps) => {
  const { platform } = useGlobalContext();
  const { web } = useWebView();
  const { firebase } = useAnalytics();
  const router = useRouter();

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      {isBorder && <Border type={BorderType.space} />}
      <Carousel
        autoPlay={autoPlay}
        showIndicators={items.length > 1}
        showThumbs={false}
        showStatus={false}
        showArrows={false}
        infiniteLoop
        emulateTouch
        onClickItem={(i) => {
          if (name === 'play-full-screen=banner') {
            firebase.logEvent('chatbot_click_play_curation', {
              curation_view_type: 'full_screen_banner' as CurationViewType,
            });
          }
          const { url } = items[i];
          if (url) {
            if (platform === Platform.web) {
              router.push(url);
            }
            web.openUrl(url);
          }
        }}
      >
        {items.map((item, idx) => (
          <div
            key={`banner-${name}-${item.id}`}
            css={bannerItemCSS(
              size ?? (item.image.attachment.height ?? 150) / 3
            )}
          >
            <OptimizeEFinderImage
              image={item.image}
              h={EFinderImageSizes.banner}
              loading={idx === 0 && priority ? 'eager' : 'lazy'}
              priority={idx === 0 && priority}
            />
          </div>
        ))}
      </Carousel>
    </>
  );
};

export default Banner;

export const enum BannerKey {
  story = 'story',
  myStory = 'my_story',
}

type KeyBannerProps = {
  bKey: BannerKey | string;
  size?: BannerSize;
};

export const KeyBanner = ({ bKey, size }: KeyBannerProps) => {
  const query = useQuery(['banners', bKey], () => fetchBanners(bKey), {
    suspense: false,
    staleTime: Infinity,
    cacheTime: Infinity,
  });
  const { data } = query;

  if (!data) {
    return null;
  }

  return <Banner name={bKey} size={size} items={data} />;
};
